import { TextField, Box, InputAdornment } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { useApiAccessService } from 'services/api-access.service';
import { API, TenantConfigContext } from 'context/TenantConfigContext';
import { PageContainer, PageSectionTitle, Tile } from 'components/Layout/PageContainer';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { toast } from 'sonner';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ActiveTokensTable } from './ActiveTokensTable';
import { ApiTeaser } from './components/ApiTeaser';
import { Button } from 'components/Button';

interface IntegrationStep {
  number: number;
  titleId: string;
  titleDefault?: string;
  descriptionId: string;
  descriptionDefault?: string;
}

const renderTechnicalLink = (chunks: React.ReactNode) => (
  <Link to="/api/documentation" className="text-[#0B9AAB] hover:underline">
    {chunks}
  </Link>
);

function ApiAccessToken() {
  const { permissions, isSuperAdmin, isSupportAdmin, isAdminView } = useContext(TenantConfigContext);
  const hasApiPermission = useMemo((): boolean => {
    if (isSuperAdmin) {
      return isAdminView || permissions.includes(API);
    }
    if (isSupportAdmin) {
      return false;
    }
    return permissions.includes(API);
  }, [permissions, isSuperAdmin, isSupportAdmin, isAdminView]);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const apiAccessService = useApiAccessService();
  const codeForWhichExchangeAttempted = useRef<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const currentCodeInUrl = urlParams.get('code');

    if (currentCodeInUrl && currentCodeInUrl !== codeForWhichExchangeAttempted.current) {
      codeForWhichExchangeAttempted.current = currentCodeInUrl;
      setIsLoading(true);

      apiAccessService
        .exchangeCodeForToken(currentCodeInUrl)
        .then(({ data: exchangeData, error: exchangeError }) => {
          if (exchangeError) {
            toast.error('The Refresh Token could not be generated. Please try again later.');
          } else if (exchangeData?.refreshToken) {
            setToken(exchangeData.refreshToken);
            toast.success('The Refresh Token generation successful.');
            navigate(location.pathname + (location.hash || ''), {
              replace: true,
            });
          } else {
            toast.error('The Refresh Token could not be generated. Please try again later.');
          }
        })
        .catch(err => {
          toast.error('The Refresh Token could not be generated. Please try again later.');
          console.error('Error exchanging code for token:', err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else if (!currentCodeInUrl) {
      codeForWhichExchangeAttempted.current = null;
    }
  }, [apiAccessService, location, navigate]);

  const integrationSteps: IntegrationStep[] = [
    {
      number: 1,
      titleId: 'api_access.token.step1_title',
      descriptionId: 'api_access.token.step1_description',
    },
    {
      number: 2,
      titleId: 'api_access.token.step2_title',
      descriptionId: 'api_access.token.step2_description',
    },
    {
      number: 3,
      titleId: 'api_access.token.step3_title',
      descriptionId: 'api_access.token.step3_description',
    },
    {
      number: 4,
      titleId: 'api_access.token.step4_title',
      descriptionId: 'api_access.token.step4_description',
    },
  ];

  const generateToken = async () => {
    try {
      setIsLoading(true);
      toast.info('Refresh Token generation in progress...');

      await new Promise(resolve => {
        setTimeout(resolve, 2000);
      });

      const {
        data,
        error: apiError,
      }: {
        data: { redirectUrl: string } | null;
        error: { message: string } | null;
      } = await apiAccessService.generateToken();

      if (apiError) {
        toast.error('The Refresh Token could not be generated. Please try again later.');
        setIsLoading(false);
        return;
      }

      if (data?.redirectUrl) {
        window.location.href = data.redirectUrl;
      } else {
        toast.error('Could not initiate token generation. Please try again.');
        setIsLoading(false);
      }
    } catch (err) {
      toast.error('The Refresh Token could not be generated. Please try again later.');
      console.error('Error generating token:', err);
      setIsLoading(false);
    }
  };

  const handleGenerateToken = () => {
    generateToken();
  };

  const copyToken = () => {
    if (token) {
      navigator.clipboard.writeText(token);
    }
  };

  let tokenDisplayArea;
  if (token ?? isLoading) {
    tokenDisplayArea = (
      <div className="h-full w-full border border-gray-200 bg-snow-300">
        <TextField
          data-testid="api-technical-setup-token-textbox"
          fullWidth
          value={token ?? 'Generating...'}
          type={isLoading ? 'text' : 'password'}
          InputProps={{
            readOnly: true,
            className: 'h-full bg-transparent',
            classes: {
              input: 'h-full flex items-center',
              notchedOutline: 'border-0',
            },
            endAdornment: token && (
              <InputAdornment position="end">
                <Button aria-label="copy token to clipboard" onClick={copyToken} className="ml-2" icon="Copy" />
              </InputAdornment>
            ),
          }}
          className="h-full"
        />
      </div>
    );
  } else {
    tokenDisplayArea = (
      <div
        className="h-full flex items-center px-4 border border-gray-200"
        data-testid="api-technical-setup-no-token-generated"
      >
        <p>
          <FormattedMessage id="api_access.token.no_token" />
        </p>
      </div>
    );
  }

  return (
    <PageContainer>
      {!hasApiPermission ? (
        <ApiTeaser />
      ) : (
        <>
          <PageSectionTitle titleId="api_access.token.title" />

          <Tile>
            <p className="mb-4">
              <FormattedMessage
                id="api_access.token.description"
                values={{
                  br: <br />,
                }}
              />
            </p>

            <div className="flex items-center gap-2">
              <div className="flex-grow h-10">{tokenDisplayArea}</div>
              {isLoading ? (
                <div className="w-44 flex justify-center">
                  <LoadingSpinner size="medium" />
                </div>
              ) : (
                <Button onClick={handleGenerateToken} className="w-44">
                  <FormattedMessage id={token ? 'api_access.token.regenerate' : 'api_access.token.start_generating'} />
                </Button>
              )}
            </div>
          </Tile>
        </>
      )}
      <PageSectionTitle titleId="api_access.token.integrate_title" />

      {integrationSteps.map(step => (
        <Tile key={step.number}>
          <Box className="flex items-center">
            <span className="text-[#0B9AAB] text-lg mr-4 font-bold">{step.number}</span>
            <Box>
              <span className="font-bold block">
                <FormattedMessage id={step.titleId} defaultMessage={step.titleDefault} />
              </span>
              <span>
                <FormattedMessage
                  id={step.descriptionId}
                  defaultMessage={step.descriptionDefault}
                  values={{
                    br: <br />,
                    technicalLink: renderTechnicalLink,
                  }}
                />
              </span>
            </Box>
          </Box>
        </Tile>
      ))}
      {hasApiPermission && (
        <>
          <PageSectionTitle titleId="api_access.token.active_tokens_title" />
          <Tile>
            <ActiveTokensTable />
          </Tile>
        </>
      )}
    </PageContainer>
  );
}

export { ApiAccessToken };
